// Signage → Biblioteca de medios (/signage/medios). Repositorio central: imágenes
// (se reescalan y convierten a WebP en el navegador, se deduplican por hash y se
// guardan como data-URI) y videos (por URL externa + póster opcional). Un recurso se
// sube una vez y se reutiliza en escenas de plantilla y en banners de retail media.
import { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Film, Image as ImageIcon, Link2, Pencil, Scissors, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/api/client';
import { Button } from '@/components/ui/Button';
import { Campo, Modal, fieldCls } from '@/features/pos/shared';
import { MediaAsset, procesarImagen, sha256Hex, sigList, sigRpc, sigUpdate } from './shared';

const kb = (b: number) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

const MAX_VIDEO = 25 * 1024 * 1024; // 25 MB por clip (base64 va en una sola petición)

export function Medios() {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const recorteRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLInputElement>(null);
  const [filtro, setFiltro] = useState<'todos' | 'imagen' | 'video'>('todos');
  const [subiendo, setSubiendo] = useState(false);
  const [video, setVideo] = useState<{ nombre: string; url: string } | null>(null);
  const [foco, setFoco] = useState<MediaAsset | null>(null);
  const [renom, setRenom] = useState<MediaAsset | null>(null);

  const { data: medios = [] } = useQuery<MediaAsset[]>({
    queryKey: ['sig-media'], queryFn: () => sigList<MediaAsset>('media?select=*&deleted_at=is.null&order=created_at.desc'), staleTime: 0,
    refetchInterval: (q) => ((q.state.data ?? []).some((m) => m.tipo === 'video' && !m.url && !m.poster) ? 4000 : false),
  });
  const invalidar = () => qc.invalidateQueries({ queryKey: ['sig-media'] });
  const filtrados = useMemo(() => medios.filter((m) => filtro === 'todos' || m.tipo === filtro), [medios, filtro]);
  const totalBytes = useMemo(() => medios.reduce((s, m) => s + Number(m.bytes || 0), 0), [medios]);

  const subirImagenes = async (files: FileList, recortar = false) => {
    setSubiendo(true);
    let nuevos = 0, repes = 0, sinRec = 0;
    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) continue;
        const { recortado, ...proc } = await procesarImagen(file, 1920, recortar);
        if (recortar && !recortado) sinRec += 1;
        const r = await sigRpc<{ id: string; reuso: boolean }>('sig_media_guardar', { _data: { ...proc, nombre: file.name.replace(/\.[^.]+$/, '') } });
        if (r.reuso) repes += 1; else nuevos += 1;
      }
      invalidar();
      toast.success(`${nuevos} subida(s)${repes ? `, ${repes} ya existía(n)` : ''}`);
      if (sinRec) toast.warning(`${sinRec} imagen(es) sin recorte posible (el fondo no es uniforme/claro) — se subieron tal cual`);
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setSubiendo(false); if (fileRef.current) fileRef.current.value = ''; if (recorteRef.current) recorteRef.current.value = ''; }
  };

  const subirVideoArchivo = async (file: File) => {
    if (!file.type.startsWith('video/')) { toast.error('Selecciona un archivo de video'); return; }
    if (file.size > MAX_VIDEO) { toast.error('El video no debe superar 25 MB'); return; }
    setSubiendo(true);
    try {
      const dataUri: string = await new Promise((res, rej) => { const rd = new FileReader(); rd.onload = () => res(String(rd.result)); rd.onerror = () => rej(new Error('No se pudo leer el archivo')); rd.readAsDataURL(file); });
      const b64 = dataUri.slice(dataUri.indexOf(',') + 1);
      const hash = await sha256Hex(b64);
      const r = await sigRpc<{ reuso: boolean }>('sig_media_subir_archivo', { _data: { nombre: file.name.replace(/\.[^.]+$/, ''), filename: file.name, mime: file.type, b64, bytes: file.size, hash } });
      invalidar();
      toast.success(r.reuso ? 'Ese video ya existía' : 'Video subido — procesando…');
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setSubiendo(false); if (videoRef.current) videoRef.current.value = ''; }
  };

  const guardarVideo = async () => {
    if (!video?.url) { toast.error('La URL es obligatoria'); return; }
    setSubiendo(true);
    try {
      const hash = 'url:' + (await sha256Hex(video.url.trim()));
      await sigRpc('sig_media_guardar', { _data: { tipo: 'video', nombre: video.nombre || video.url, url: video.url.trim(), hash } });
      setVideo(null); invalidar(); toast.success('Video agregado');
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setSubiendo(false); }
  };

  const eliminar = (m: MediaAsset) => sigUpdate('media', m.id, { deleted_at: new Date().toISOString() }).then(invalidar).then(() => toast.success('Recurso eliminado')).catch((e) => toast.error(getErrorMessage(e)));
  const guardarFoco = async () => { if (!foco) return; try { await sigUpdate('media', foco.id, { foco_x: foco.foco_x, foco_y: foco.foco_y }); setFoco(null); invalidar(); toast.success('Punto focal guardado'); } catch (e) { toast.error(getErrorMessage(e)); } };
  const guardarNombre = async () => { if (!renom) return; try { await sigUpdate('media', renom.id, { nombre: renom.nombre }); setRenom(null); invalidar(); toast.success('Renombrado'); } catch (e) { toast.error(getErrorMessage(e)); } };

  const clickFoco = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!foco) return;
    const r = e.currentTarget.getBoundingClientRect();
    setFoco({ ...foco, foco_x: Math.round(((e.clientX - r.left) / r.width) * 100), foco_y: Math.round(((e.clientY - r.top) / r.height) * 100) });
  };

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-xl font-semibold flex items-center gap-2"><ImageIcon className="w-5 h-5" /> Biblioteca de medios</h1>
        <div className="ml-auto flex items-center gap-2">
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => e.target.files?.length && subirImagenes(e.target.files)} />
          <input ref={recorteRef} type="file" accept="image/*" multiple hidden onChange={(e) => e.target.files?.length && subirImagenes(e.target.files, true)} />
          <input ref={videoRef} type="file" accept="video/*" hidden onChange={(e) => e.target.files?.[0] && subirVideoArchivo(e.target.files[0])} />
          <Button variant="outline" onClick={() => fileRef.current?.click()} isLoading={subiendo}><Upload className="w-4 h-4 mr-1" /> Subir imágenes</Button>
          <Button variant="outline" onClick={() => recorteRef.current?.click()} isLoading={subiendo} title="Quita el fondo blanco/claro (foto de estudio) y guarda con transparencia — ideal para la escena Marco roto"><Scissors className="w-4 h-4 mr-1" /> Subir sin fondo</Button>
          <Button variant="outline" onClick={() => videoRef.current?.click()} isLoading={subiendo}><Film className="w-4 h-4 mr-1" /> Subir video</Button>
          <Button variant="outline" onClick={() => setVideo({ nombre: '', url: '' })}><Link2 className="w-4 h-4 mr-1" /> Video por URL</Button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground">Sube una vez y reutiliza en escenas (imagen/video) y en banners de retail media. Las imágenes se convierten a <strong>WebP</strong>, se reescalan a máx. 1920 px y se <strong>deduplican</strong> por contenido. Ajusta el <strong>punto focal</strong> para controlar el recorte en pantalla. <strong>Subir sin fondo</strong> quita el fondo de fotos de estudio (fondo claro uniforme) y guarda con transparencia — para la escena <strong>Marco roto</strong> (efecto 3D).</p>

      <div className="flex items-center gap-3">
        <div className="inline-flex rounded-full border overflow-hidden text-sm">
          {([['todos', 'Todos'], ['imagen', 'Imágenes'], ['video', 'Videos']] as const).map(([v, l]) => (
            <button key={v} className={`px-3 py-1.5 ${filtro === v ? 'bg-foreground text-background font-medium' : 'text-muted-foreground'}`} onClick={() => setFiltro(v)}>{l}</button>
          ))}
        </div>
        <span className="text-xs text-muted-foreground ml-auto">{medios.length} recurso(s) · {kb(totalBytes)} almacenados</span>
      </div>

      {!filtrados.length ? (
        <div className="border rounded-lg py-12 text-center text-muted-foreground text-sm">Sin recursos. Sube imágenes o agrega un video por URL.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {filtrados.map((m) => (
            <div key={m.id} className="border rounded-lg overflow-hidden group">
              <div className="aspect-video bg-muted flex items-center justify-center overflow-hidden relative">
                {m.tipo === 'imagen' && m.datos ? <img src={m.datos} alt={m.nombre} className="w-full h-full object-cover" style={{ objectPosition: `${m.foco_x}% ${m.foco_y}%` }} />
                  : m.poster ? <img src={m.poster} alt={m.nombre} className="w-full h-full object-cover" />
                  : <Film className="w-8 h-8 text-muted-foreground" />}
                {m.tipo === 'video' && !m.url && !m.poster && <span className="absolute inset-0 flex items-center justify-center bg-black/50 text-white text-xs">procesando…</span>}
                <span className="absolute top-1 left-1 text-[10px] font-medium uppercase px-1.5 py-0.5 rounded bg-black/60 text-white">{m.tipo}</span>
              </div>
              <div className="p-2 space-y-1">
                <div className="text-xs font-medium truncate" title={m.nombre}>{m.nombre}</div>
                <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                  {m.tipo === 'imagen' ? `${m.ancho}×${m.alto} · ${kb(m.bytes)}` : (m.url || '').slice(0, 22) + '…'}
                </div>
                <div className="flex gap-1 pt-1">
                  {m.tipo === 'imagen' && <button className="inline-flex w-7 h-7 items-center justify-center rounded hover:bg-muted" title="Punto focal" onClick={() => setFoco(m)}>🎯</button>}
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded hover:bg-muted" title="Renombrar" onClick={() => setRenom(m)}><Pencil className="w-4 h-4" /></button>
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded hover:bg-muted ml-auto" title="Eliminar" onClick={() => void eliminar(m)}><Trash2 className="w-4 h-4 text-red-500" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {video && (
        <Modal title="Agregar video por URL" onClose={() => setVideo(null)}>
          <div className="space-y-3">
            <Campo label="Nombre"><input className={fieldCls} value={video.nombre} onChange={(e) => setVideo({ ...video, nombre: e.target.value })} autoFocus /></Campo>
            <Campo label="URL del video (MP4/WebM, YouTube o Vimeo)"><input className={fieldCls} value={video.url} onChange={(e) => setVideo({ ...video, url: e.target.value })} placeholder="https://…" /></Campo>
            <p className="text-xs text-muted-foreground">El video se referencia por URL (no se aloja aquí). Para hospedar el archivo propio hará falta un servidor de medios (pendiente).</p>
            <div className="flex justify-end gap-2 pt-2"><Button variant="ghost" onClick={() => setVideo(null)}>Cancelar</Button><Button onClick={() => void guardarVideo()} isLoading={subiendo}>Agregar</Button></div>
          </div>
        </Modal>
      )}

      {foco && foco.datos && (
        <Modal title="Punto focal — clic donde debe centrarse el recorte" onClose={() => setFoco(null)}>
          <div className="space-y-3">
            <div className="relative w-full overflow-hidden rounded-lg border cursor-crosshair select-none" onClick={clickFoco}>
              <img src={foco.datos} alt={foco.nombre} className="w-full max-h-[50vh] object-contain bg-muted" />
              <div className="absolute w-6 h-6 -ml-3 -mt-3 rounded-full border-2 border-white ring-2 ring-black/50 pointer-events-none" style={{ left: `${foco.foco_x}%`, top: `${foco.foco_y}%` }} />
            </div>
            <div className="text-xs text-muted-foreground">Foco: {foco.foco_x}% / {foco.foco_y}% — se usa como <code>object-position</code> al recortar en pantalla.</div>
            <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setFoco(null)}>Cancelar</Button><Button onClick={() => void guardarFoco()}>Guardar</Button></div>
          </div>
        </Modal>
      )}

      {renom && (
        <Modal title="Renombrar recurso" onClose={() => setRenom(null)}>
          <div className="space-y-3">
            <Campo label="Nombre"><input className={fieldCls} value={renom.nombre} onChange={(e) => setRenom({ ...renom, nombre: e.target.value })} autoFocus /></Campo>
            <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setRenom(null)}>Cancelar</Button><Button onClick={() => void guardarNombre()}>Guardar</Button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}
